"use client";

import { useState } from "react";
import type { Map as MLMap } from "maplibre-gl";
import { Badge, Button, Download, FileText, Globe, Icon, Image, Layers, Modal, cx } from "@/components";
import type { MapSpec, PointSet } from "@/lib/map";
import { buildMapPdf, toGeoJson, toKml } from "@/lib/share";
import "./share.css";

export type ExportFormat = "png" | "kml" | "geojson" | "pdf";

export function isDataExportFormat(format: ExportFormat): boolean {
  return format === "kml" || format === "geojson";
}

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  pointSet: PointSet;
  spec: MapSpec;
  title: string;
  /** Non-removable attribution string — stamped onto every export. */
  attribution: string;
  /** The live MapLibre map (via MapCanvas onReady) — the PNG capture source. */
  getMap: () => MLMap | null;
  onError: (message: string) => void;
  /** KML/GeoJSON are Pro data exports; PNG/PDF remain available to everyone. */
  dataExportAllowed?: boolean;
  /** Opens the shared Pro upsell when a locked data export is attempted. */
  onDataExportLocked?: () => void;
}

function fileSlug(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug !== "" ? slug : "map";
}

function download(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Decode a base64 `data:` URL to raw bytes WITHOUT fetch(). The app CSP's
 * `connect-src` doesn't allow `data:`, so `fetch(dataUrl)` throws a CSP
 * violation — which is exactly why PDF export failed while PNG (which never
 * fetches — it uses an <img>, allowed by `img-src data:`) worked.
 */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Read a paint value off the live token sheet (canvas can't use CSS vars). */
function tokenColor(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value !== "" ? value : fallback;
}

/**
 * Capture the live WebGL canvas. MapLibre runs without preserveDrawingBuffer,
 * so the read must happen synchronously inside a render frame — repaint, then
 * read the buffer in the render handler before the browser swaps it out.
 */
function captureMap(map: MLMap): Promise<string> {
  return new Promise((resolve) => {
    map.once("render", () => resolve(map.getCanvas().toDataURL("image/png")));
    map.triggerRepaint();
  });
}

/** Compose the capture with the attribution chip — exports always carry it. */
async function composePng(dataUrl: string, attribution: string): Promise<Blob | null> {
  const img = document.createElement("img");
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("capture decode failed"));
    img.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);

  const scale = Math.max(1, Math.round(img.width / 1200));
  const pad = 6 * scale;
  const fontPx = 11 * scale;
  ctx.font = `600 ${fontPx}px ${tokenColor("--type-family-mono", "monospace")}`;
  const text = attribution.toUpperCase();
  const metrics = ctx.measureText(text);
  const chipW = metrics.width + pad * 2;
  const chipH = fontPx + pad * 2;
  const x = canvas.width - chipW - 8 * scale;
  const y = canvas.height - chipH - 8 * scale;

  ctx.fillStyle = tokenColor("--color-surface-raised", "white");
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(x, y, chipW, chipH, 4 * scale);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, chipW, chipH);
  }
  ctx.fillStyle = tokenColor("--color-text-secondary", "black");
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + pad, y + chipH / 2);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

/**
 * The M6 export dialog — three formats, one decision each: a picture (PNG of
 * the current view), the data (KML for Google Earth / GIS), or a page (PDF,
 * deferred). The current map state — filters, colors, viewport — is what
 * exports, and the attribution rides along in every format.
 */
export function ExportDialog({
  open,
  onClose,
  pointSet,
  spec,
  title,
  attribution,
  getMap,
  onError,
  dataExportAllowed = true,
  onDataExportLocked,
}: ExportDialogProps) {
  const [format, setFormat] = useState<ExportFormat>("png");
  const [busy, setBusy] = useState(false);

  const formats: Array<{
    id: ExportFormat;
    name: string;
    meta: string;
    desc: string;
    icon: typeof Image;
  }> = [
    {
      id: "png",
      name: "PNG",
      meta: "current view",
      desc: "A crisp snapshot of the map as styled right now, pins included.",
      icon: Image,
    },
    {
      id: "kml",
      name: "KML",
      meta: `${pointSet.count.toLocaleString()} points`,
      desc: "Every point with its data, for Google Earth and GIS tools.",
      icon: Globe,
    },
    {
      id: "geojson",
      name: "GeoJSON",
      meta: `${pointSet.count.toLocaleString()} points`,
      desc: "Standard GeoJSON for mapping libraries, QGIS, and data pipelines.",
      icon: Layers,
    },
    {
      id: "pdf",
      name: "PDF",
      meta: "print-ready",
      desc: "A print-ready page: the map, its title, and the attribution footer.",
      icon: FileText,
    },
  ];

  async function exportPng() {
    const map = getMap();
    if (!map) {
      onError("The map isn't ready yet — try again in a moment.");
      return;
    }
    setBusy(true);
    try {
      const blob = await composePng(await captureMap(map), attribution);
      if (!blob) throw new Error("compose failed");
      download(`${fileSlug(title)}.png`, blob);
      onClose();
    } catch {
      onError("PNG export failed — try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  async function exportPdf() {
    const map = getMap();
    if (!map) {
      onError("The map isn't ready yet — try again in a moment.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await captureMap(map);
      const pngBytes = dataUrlToBytes(dataUrl);
      const bytes = await buildMapPdf({
        pngBytes,
        title,
        attribution,
        inkColor: tokenColor("--color-text-primary", "black"),
        mutedColor: tokenColor("--color-text-muted", "dimgray"),
      });
      download(`${fileSlug(title)}.pdf`, new Blob([bytes as BlobPart], { type: "application/pdf" }));
      onClose();
    } catch {
      onError("PDF export failed — try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  function exportKml() {
    const kml = toKml(pointSet, {
      documentName: `${title} — ${attribution}`,
      titleColumn: spec.popup.titleColumn,
      fields: pointSet.columns.map((c) => c.name),
    });
    download(`${fileSlug(title)}.kml`, new Blob([kml], { type: "application/vnd.google-earth.kml+xml" }));
    onClose();
  }

  function exportGeoJson() {
    const geojson = toGeoJson(pointSet, {
      titleColumn: spec.popup.titleColumn,
      fields: pointSet.columns.map((c) => c.name),
    });
    download(`${fileSlug(title)}.geojson`, new Blob([geojson], { type: "application/geo+json" }));
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Export this map" className="export-modal">
      <p className="export-sub">Exports the map as it looks right now — filters and colors included.</p>

      <div className="export-formats" role="radiogroup" aria-label="Export format">
        {formats.map((f) => (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={format === f.id}
            className={cx("export-card", format === f.id && "export-card--selected")}
            onClick={() => setFormat(f.id)}
          >
            <span className="export-card__icon">
              <Icon icon={f.icon} size="lg" />
            </span>
            <span className="export-card__body">
              <span className="export-card__head">
                <span className="export-card__name">{f.name}</span>
                {isDataExportFormat(f.id) && !dataExportAllowed ? <Badge variant="accent">PRO</Badge> : null}
                <span className="export-card__meta export-card__meta--mono">{f.meta}</span>
              </span>
              <span className="export-card__desc">{f.desc}</span>
            </span>
            <span aria-hidden className={cx("export-card__radio", format === f.id && "export-card__radio--on")} />
          </button>
        ))}
      </div>

      <Button
        className="export-go"
        loading={busy}
        iconLeading={busy ? undefined : <Icon icon={Download} size="sm" />}
        onClick={() => {
          if (isDataExportFormat(format) && !dataExportAllowed) {
            onClose();
            onDataExportLocked?.();
            return;
          }
          if (format === "png") void exportPng();
          else if (format === "kml") exportKml();
          else if (format === "geojson") exportGeoJson();
          else if (format === "pdf") void exportPdf();
        }}
      >
        Export {format.toUpperCase()}
      </Button>
      <p className="export-note">Every export carries the {attribution} attribution.</p>
    </Modal>
  );
}
